import { useState, useEffect } from 'react'
import {
  Zap,
  Search,
  Sparkles,
  Check,
  X,
  AlertCircle,
  Pencil,
  Save,
  RotateCcw,
  ExternalLink
} from 'lucide-react'
import {
  categoryManagerApi,
  CategoryDetail,
  CategorySearchResult,
  CategoryJobStatusResponse
} from '@/lib/api'
import { OpenAICostDisplay } from '@/components/OpenAICostDisplay'
import { useToast } from '@/components/ui/Toast'

interface ProgressState {
  step: number
  total: number
  message: string
  percentage: number
}

export default function CategoryOptimizerPage() {
  const toast = useToast()
  // Search state
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<CategorySearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  // Selected category
  const [selectedCategory, setSelectedCategory] = useState<CategoryDetail | null>(null)
  const [loadingCategory, setLoadingCategory] = useState(false)

  // Optimization options
  const [instructions, setInstructions] = useState('')
  const [aiModel, setAiModel] = useState<string>('gpt-4o-mini')
  const [generateImage, setGenerateImage] = useState(false)

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false)
  const [progress, setProgress] = useState<ProgressState | null>(null)
  const [previewData, setPreviewData] = useState<CategoryJobStatusResponse['result'] | null>(null)
  const [generationError, setGenerationError] = useState<string | null>(null)

  // Apply state
  const [isApplying, setIsApplying] = useState(false)
  const [applyProgress, setApplyProgress] = useState<string | null>(null)
  const [applyResult, setApplyResult] = useState<{
    success: boolean
    message: string
    backupId?: number
  } | null>(null)

  // Editing state
  const [editingField, setEditingField] = useState<string | null>(null)
  const [editedValues, setEditedValues] = useState<Record<string, string>>({})

  // Search categories
  const handleSearch = async () => {
    if (!searchQuery.trim()) return

    setIsSearching(true)
    setSearchResults([])
    setSelectedCategory(null)
    setPreviewData(null)
    setApplyResult(null)
    setHasSearched(false)

    try {
      const results = await categoryManagerApi.searchCategories(searchQuery.trim())
      setSearchResults(results)
      setHasSearched(true)
    } catch (error) {
      console.error('Error searching categories:', error)
      setHasSearched(true)
    } finally {
      setIsSearching(false)
    }
  }

  // Select category
  const handleSelectCategory = async (categoryId: number) => {
    setLoadingCategory(true)
    setPreviewData(null)
    setApplyResult(null)
    setEditedValues({})
    setGenerationError(null)

    try {
      const category = await categoryManagerApi.getCategory(categoryId)
      setSelectedCategory(category)
      setSearchResults([])
      setHasSearched(false)
    } catch (error: any) {
      console.error('Error loading category:', error)
      setGenerationError(`Error al cargar la categoría: ${error.response?.data?.detail || error.message}`)
      // Keep search results visible so user can try again
    } finally {
      setLoadingCategory(false)
    }
  }

  // Generate optimization
  const handleOptimize = async () => {
    if (!selectedCategory) return

    setIsGenerating(true)
    setProgress(null)
    setPreviewData(null)
    setGenerationError(null)
    setEditedValues({})

    try {
      const startResponse = await categoryManagerApi.startOptimize({
        category_id: selectedCategory.id_category,
        instructions,
        model: aiModel,
        generate_image: generateImage
      })

      const jobId = startResponse.job_id

      // Poll for status
      const pollInterval = setInterval(async () => {
        try {
          const status = await categoryManagerApi.getOptimizeStatus(jobId)

          if (status.progress) {
            setProgress({
              step: status.progress.step,
              total: status.progress.total,
              message: status.progress.message,
              percentage: status.progress.percentage
            })
          }

          if (status.status === 'completed' && status.result) {
            clearInterval(pollInterval)
            setPreviewData(status.result)
            setIsGenerating(false)
          } else if (status.status === 'failed') {
            clearInterval(pollInterval)
            setGenerationError(status.error || 'Error desconocido')
            setIsGenerating(false)
          }
        } catch (error) {
          clearInterval(pollInterval)
          setGenerationError('Error al consultar el estado')
          setIsGenerating(false)
        }
      }, 1000)

    } catch (error: any) {
      setGenerationError(error.response?.data?.detail || 'Error al iniciar optimización')
      setIsGenerating(false)
    }
  }

  // Apply changes
  const handleApply = async () => {
    if (!selectedCategory || !previewData?.optimized) return

    setIsApplying(true)
    setApplyProgress('Preparando traducción a todos los idiomas...')

    try {
      const spanishData = {
        name: editedValues.name ?? previewData.optimized.name,
        description: editedValues.description ?? previewData.optimized.description,
        additional_description: editedValues.additional_description ?? previewData.optimized.additional_description,
        link_rewrite: editedValues.link_rewrite ?? previewData.optimized.link_rewrite,
        meta_title: editedValues.meta_title ?? previewData.optimized.meta_title,
        meta_description: editedValues.meta_description ?? previewData.optimized.meta_description
      }

      setApplyProgress('Traduciendo a PT, EN, FR, DE...')

      const result = await categoryManagerApi.translateAndApply({
        category_id: selectedCategory.id_category,
        id_parent: selectedCategory.id_parent,
        spanish_data: spanishData,
        json_data: previewData.json_data || {},
        model: aiModel,
        image_url: previewData.image_url  // Include image URL if generated
      })

      // Update progress if image is being uploaded
      if (previewData.image_url) {
        setApplyProgress('Subiendo imagen de categoría...')
      }

      setApplyProgress(null)
      setApplyResult({
        success: result.success,
        message: result.message,
        backupId: result.backup_id
      })

    } catch (error: any) {
      setApplyProgress(null)
      setApplyResult({
        success: false,
        message: error.response?.data?.detail || 'Error al aplicar cambios'
      })
    } finally {
      setIsApplying(false)
    }
  }

  // Rollback
  const handleRollback = async () => {
    if (!applyResult?.backupId) return

    try {
      const result = await categoryManagerApi.rollback(applyResult.backupId)
      setApplyResult({
        success: true,
        message: result.message
      })
      setPreviewData(null)
      toast.success('Cambios deshechos correctamente')
    } catch (error: any) {
      toast.error('Error al deshacer cambios', error.response?.data?.detail || 'Error desconocido')
    }
  }

  // Field editing
  const startEditing = (field: string) => setEditingField(field)

  const saveEdit = (field: string, value: string) => {
    setEditedValues(prev => ({ ...prev, [field]: value }))
    setEditingField(null)
  }

  const cancelEdit = () => setEditingField(null)

  const getFieldValue = (field: string, source: 'current' | 'optimized') => {
    if (source === 'optimized' && editedValues[field] !== undefined) {
      return editedValues[field]
    }
    if (source === 'current') {
      return previewData?.current?.[field] ?? ''
    }
    return previewData?.optimized?.[field] ?? ''
  }

  const isFieldEdited = (field: string) => editedValues[field] !== undefined

  // Get Spanish content from selected category (lang_id 1 = Spanish)
  // Note: JSON serializes numeric keys as strings, so we check both
  const getSpanishContent = (): {
    name?: string
    description?: string
    link_rewrite?: string
    meta_title?: string
    meta_description?: string
  } => {
    const langs = selectedCategory?.languages
    if (!langs) return {}
    // Try both numeric and string key access
    return langs[1] || langs['1' as unknown as number] || {}
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <Zap className="w-8 h-8 text-yellow-500" />
            Optimizar Categoría Web
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Mejora el contenido SEO de categorías existentes con IA
          </p>
        </div>
      </div>

      {/* Search Section */}
      {!selectedCategory && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Buscar categoría por ID o nombre..."
                className="w-full pl-10 pr-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={isSearching || !searchQuery.trim()}
              className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
            >
              <Search className="w-5 h-5" />
              Buscar
            </button>
          </div>

          {/* Search Results */}
          {isSearching && (
            <div className="mt-4 flex items-center gap-2 text-slate-600 dark:text-slate-400">
              <div className="animate-spin w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full"></div>
              Buscando...
            </div>
          )}

          {searchResults.length > 0 && (
            <div className="mt-4 border border-slate-200 dark:border-slate-700 rounded-lg divide-y divide-slate-200 dark:divide-slate-700">
              {searchResults.map((cat) => (
                <div
                  key={cat.id_category}
                  onClick={() => handleSelectCategory(cat.id_category)}
                  className="p-4 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm text-slate-500 dark:text-slate-400 mr-2">
                        #{cat.id_category}
                      </span>
                      <span className="font-medium text-slate-900 dark:text-white">
                        {cat.name}
                      </span>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${
                      cat.active
                        ? 'bg-success-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-danger-100 text-red-800 dark:bg-danger-900 dark:text-red-200'
                    }`}>
                      {cat.active ? 'Activa' : 'Inactiva'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {searchResults.length === 0 && hasSearched && !isSearching && (
            <p className="mt-4 text-slate-500 dark:text-slate-400">
              No se encontraron categorías para "{searchQuery}"
            </p>
          )}

          {/* Error loading category */}
          {generationError && !selectedCategory && (
            <div className="mt-4 p-4 bg-danger-50 dark:bg-danger-900/20 rounded-lg border border-red-200 dark:border-red-700">
              <div className="flex items-center gap-2 text-danger-700 dark:text-red-300">
                <AlertCircle className="w-5 h-5" />
                <span>{generationError}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Loading Category */}
      {loadingCategory && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
          <div className="flex items-center gap-2">
            <div className="animate-spin w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full"></div>
            Cargando categoría...
          </div>
        </div>
      )}

      {/* Selected Category */}
      {selectedCategory && !previewData && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
              #{selectedCategory.id_category} - {getSpanishContent().name}
            </h2>
            <button
              onClick={() => {
                setSelectedCategory(null)
                setSearchQuery('')
                setHasSearched(false)
              }}
              className="text-slate-500 hover:text-gray-700 dark:hover:text-slate-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Current Content Preview */}
          <div className="mb-6 p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
            <h3 className="font-medium text-slate-700 dark:text-slate-300 mb-2">Contenido actual (ES)</h3>
            <div className="space-y-2 text-sm text-slate-800 dark:text-gray-200">
              <p><strong>Nombre:</strong> {getSpanishContent().name}</p>
              <p><strong>Meta título:</strong> {getSpanishContent().meta_title || <span className="text-slate-400 italic">Sin definir</span>}</p>
              <p><strong>Meta descripción:</strong> {getSpanishContent().meta_description || <span className="text-slate-400 italic">Sin definir</span>}</p>
              <p><strong>URL:</strong> {getSpanishContent().link_rewrite}</p>
            </div>
            {/* Category Description */}
            <div className="mt-4">
              <strong className="text-sm text-slate-700 dark:text-slate-300">Descripción:</strong>
              {getSpanishContent().description ? (
                <div
                  className="mt-2 max-w-none text-sm text-slate-800 dark:text-gray-200 bg-white dark:bg-slate-900 p-3 rounded border border-gray-200 dark:border-gray-500 max-h-[200px] overflow-y-auto [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-2 [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1"
                  dangerouslySetInnerHTML={{ __html: getSpanishContent().description || '' }}
                />
              ) : (
                <p className="mt-1 text-slate-400 italic text-sm">Sin descripción</p>
              )}
            </div>
          </div>

          {/* Options */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Instrucciones adicionales (opcional)
              </label>
              <textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                disabled={isGenerating}
                placeholder="Ejemplo: Añadir más información sobre compatibilidad, destacar envío gratuito..."
                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white min-h-[100px]"
              />
            </div>

            <div className="flex items-center gap-4 flex-wrap">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Modelo IA
                </label>
                <select
                  value={aiModel}
                  onChange={(e) => setAiModel(e.target.value)}
                  disabled={isGenerating}
                  className="px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                >
                  <optgroup label="OpenAI">
                    <option value="gpt-4o-mini">GPT-4o Mini (Rápido)</option>
                    <option value="gpt-4o">GPT-4o (Mayor calidad)</option>
                  </optgroup>
                  <optgroup label="Google Gemini">
                    <option value="gemini-2.0-flash">Gemini 2.0 Flash (Muy rápido)</option>
                    <option value="gemini-2.5-pro">Gemini 2.5 Pro (Alta calidad)</option>
                  </optgroup>
                </select>
              </div>

              <div className="flex items-center pt-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={generateImage}
                    onChange={(e) => setGenerateImage(e.target.checked)}
                    disabled={isGenerating}
                    className="w-4 h-4 text-yellow-500 rounded"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">
                    Actualizar imagen de categoría
                  </span>
                </label>
              </div>

              <div className="flex-1"></div>

              <button
                onClick={handleOptimize}
                disabled={isGenerating}
                className="px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg disabled:opacity-50 flex items-center gap-2 font-medium"
              >
                <Sparkles className="w-5 h-5" />
                {isGenerating ? 'Optimizando...' : 'Optimizar con IA'}
              </button>
            </div>
          </div>

          {/* Progress */}
          {isGenerating && (
            <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700">
              <div className="flex items-center gap-3 mb-3">
                <div className="animate-spin w-5 h-5 border-2 border-yellow-500 border-t-transparent rounded-full"></div>
                <span className="font-medium text-yellow-800 dark:text-yellow-200">
                  {progress?.message || 'Iniciando optimización...'}
                </span>
              </div>
              {progress && (
                <div className="w-full bg-yellow-200 dark:bg-yellow-800 rounded-full h-2">
                  <div
                    className="bg-yellow-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress.percentage}%` }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Error */}
          {generationError && (
            <div className="mt-6 p-4 bg-danger-50 dark:bg-danger-900/20 rounded-lg border border-red-200 dark:border-red-700">
              <div className="flex items-center gap-2 text-danger-700 dark:text-red-300">
                <AlertCircle className="w-5 h-5" />
                <span>{generationError}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Preview / Comparison */}
      {previewData && !applyResult && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">
            Comparativa: Antes vs Después
          </h2>

          {/* OpenAI Usage */}
          {previewData.openai_usage && (
            <div className="mb-6">
              <OpenAICostDisplay usage={previewData.openai_usage} showDetails={true} />
            </div>
          )}

          {/* Generated Image */}
          {previewData.image_url && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Nueva Imagen Generada
              </h3>
              <div className="flex items-start gap-4">
                <img
                  src={previewData.image_url}
                  alt="Nueva imagen de categoría"
                  className="w-48 h-48 object-cover rounded-lg border border-slate-200 dark:border-slate-700"
                />
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  <p className="flex items-center gap-1 text-success-600 dark:text-success-400 font-medium">
                    <Check className="w-4 h-4" />
                    Esta imagen se subirá automáticamente al aplicar los cambios
                  </p>
                  <p className="mt-1">
                    La imagen se guardará en PrestaShop como imagen de la categoría.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Comparison Fields */}
          <div className="space-y-4">
            <ComparisonField
              label="Nombre"
              field="name"
              current={previewData.current?.name || ''}
              optimized={getFieldValue('name', 'optimized')}
              isEditing={editingField === 'name'}
              isEdited={isFieldEdited('name')}
              onStartEdit={() => startEditing('name')}
              onSave={(value) => saveEdit('name', value)}
              onCancel={cancelEdit}
            />

            <ComparisonField
              label="Meta Título"
              field="meta_title"
              current={previewData.current?.meta_title || ''}
              optimized={getFieldValue('meta_title', 'optimized')}
              isEditing={editingField === 'meta_title'}
              isEdited={isFieldEdited('meta_title')}
              onStartEdit={() => startEditing('meta_title')}
              onSave={(value) => saveEdit('meta_title', value)}
              onCancel={cancelEdit}
              maxLength={60}
            />

            <ComparisonField
              label="Meta Descripción"
              field="meta_description"
              current={previewData.current?.meta_description || ''}
              optimized={getFieldValue('meta_description', 'optimized')}
              isEditing={editingField === 'meta_description'}
              isEdited={isFieldEdited('meta_description')}
              onStartEdit={() => startEditing('meta_description')}
              onSave={(value) => saveEdit('meta_description', value)}
              onCancel={cancelEdit}
              maxLength={160}
              showCharCount
            />

            {/* URL Amigable - Solo lectura, no se modifica */}
            <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3">
              <span className="font-medium text-slate-700 dark:text-slate-300">URL Amigable</span>
              <span className="ml-2 text-xs text-slate-500">(no se modifica)</span>
              <p className="text-slate-600 dark:text-slate-400 mt-1 bg-slate-50 dark:bg-slate-800 p-2 rounded">
                {previewData.current?.link_rewrite || getSpanishContent().link_rewrite}
              </p>
            </div>

            {/* Description HTML Comparison - Short (above products) */}
            <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-slate-700 dark:text-slate-300">
                  Descripción Corta (arriba de productos)
                  {isFieldEdited('description') && <span className="ml-2 text-xs text-primary-600">(editado)</span>}
                </span>
                {editingField !== 'description' && (
                  <button
                    onClick={() => startEditing('description')}
                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                  >
                    <Pencil className="w-4 h-4 text-slate-500" />
                  </button>
                )}
              </div>

              {editingField === 'description' ? (
                <div className="space-y-2">
                  <textarea
                    defaultValue={getFieldValue('description', 'optimized')}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white min-h-[150px] font-mono text-sm"
                    id="edit-description"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const el = document.getElementById('edit-description') as HTMLTextAreaElement
                        saveEdit('description', el.value)
                      }}
                      className="px-3 py-1 bg-green-600 text-white rounded text-sm flex items-center gap-1"
                    >
                      <Save className="w-3 h-3" /> Guardar
                    </button>
                    <button onClick={cancelEdit} className="px-3 py-1 bg-gray-300 dark:bg-gray-600 rounded text-sm">
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">ANTES</span>
                    <div
                      className="max-w-none text-sm text-slate-800 dark:text-gray-200 bg-danger-50 dark:bg-danger-900/30 p-3 rounded max-h-[200px] overflow-y-auto border border-red-200 dark:border-red-800 [&_h3]:font-semibold [&_h3]:text-base [&_h3]:mt-3 [&_h3]:mb-2 [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_li]:mb-1 [&_strong]:font-semibold"
                      dangerouslySetInnerHTML={{ __html: previewData.current?.description || '<em class="text-slate-500">Sin descripción</em>' }}
                    />
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">DESPUÉS</span>
                    <div
                      className={`max-w-none text-sm text-slate-800 dark:text-gray-200 p-3 rounded max-h-[200px] overflow-y-auto [&_h3]:font-semibold [&_h3]:text-base [&_h3]:mt-3 [&_h3]:mb-2 [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_li]:mb-1 [&_strong]:font-semibold ${
                        isFieldEdited('description')
                          ? 'bg-primary-50 dark:bg-primary-900/30 border border-blue-200 dark:border-blue-800'
                          : 'bg-success-50 dark:bg-success-900/30 border border-green-200 dark:border-green-800'
                      }`}
                      dangerouslySetInnerHTML={{ __html: getFieldValue('description', 'optimized') || '<em class="text-slate-500">Sin descripción</em>' }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Additional Description HTML Comparison - Long (below products) */}
            <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-slate-700 dark:text-slate-300">
                  Descripción Extensa (debajo de productos) - SEO
                  {isFieldEdited('additional_description') && <span className="ml-2 text-xs text-primary-600">(editado)</span>}
                </span>
                {editingField !== 'additional_description' && (
                  <button
                    onClick={() => startEditing('additional_description')}
                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                  >
                    <Pencil className="w-4 h-4 text-slate-500" />
                  </button>
                )}
              </div>

              {editingField === 'additional_description' ? (
                <div className="space-y-2">
                  <textarea
                    defaultValue={getFieldValue('additional_description', 'optimized')}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white min-h-[300px] font-mono text-sm"
                    id="edit-additional-description"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const el = document.getElementById('edit-additional-description') as HTMLTextAreaElement
                        saveEdit('additional_description', el.value)
                      }}
                      className="px-3 py-1 bg-green-600 text-white rounded text-sm flex items-center gap-1"
                    >
                      <Save className="w-3 h-3" /> Guardar
                    </button>
                    <button onClick={cancelEdit} className="px-3 py-1 bg-gray-300 dark:bg-gray-600 rounded text-sm">
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">ANTES</span>
                    <div
                      className="max-w-none text-sm text-slate-800 dark:text-gray-200 bg-danger-50 dark:bg-danger-900/30 p-3 rounded max-h-[400px] overflow-y-auto border border-red-200 dark:border-red-800 [&_h3]:font-semibold [&_h3]:text-base [&_h3]:mt-3 [&_h3]:mb-2 [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_li]:mb-1 [&_strong]:font-semibold"
                      dangerouslySetInnerHTML={{ __html: previewData.current?.additional_description || '<em class="text-slate-500">Sin descripción adicional</em>' }}
                    />
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">DESPUÉS</span>
                    <div
                      className={`max-w-none text-sm text-slate-800 dark:text-gray-200 p-3 rounded max-h-[400px] overflow-y-auto [&_h3]:font-semibold [&_h3]:text-base [&_h3]:mt-3 [&_h3]:mb-2 [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_li]:mb-1 [&_strong]:font-semibold ${
                        isFieldEdited('additional_description')
                          ? 'bg-primary-50 dark:bg-primary-900/30 border border-blue-200 dark:border-blue-800'
                          : 'bg-success-50 dark:bg-success-900/30 border border-green-200 dark:border-green-800'
                      }`}
                      dangerouslySetInnerHTML={{ __html: getFieldValue('additional_description', 'optimized') || '<em class="text-slate-500">Sin descripción adicional</em>' }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6">
            {isApplying && applyProgress && (
              <div className="mb-4 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                <div className="flex items-center gap-3">
                  <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                  <span className="text-blue-800 dark:text-blue-200 font-medium">{applyProgress}</span>
                </div>
              </div>
            )}

            <div className="flex gap-4">
              <button
                onClick={() => setPreviewData(null)}
                disabled={isApplying}
                className="px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Descartar
              </button>
              <button
                onClick={handleApply}
                disabled={isApplying}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                <Check className="w-4 h-4" />
                {isApplying ? 'Aplicando...' : 'Confirmar y Traducir'}
              </button>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
              Al confirmar, el contenido se traducirá a PT, EN, FR, DE.
            </p>
          </div>
        </div>
      )}

      {/* Apply Result */}
      {applyResult && (
        <div className={`bg-white dark:bg-slate-800 rounded-lg shadow p-6`}>
          <div className={`p-4 rounded-lg ${
            applyResult.success
              ? 'bg-success-50 dark:bg-success-900/20 border border-green-200 dark:border-green-700'
              : 'bg-danger-50 dark:bg-danger-900/20 border border-red-200 dark:border-red-700'
          }`}>
            <div className="flex items-center gap-2">
              {applyResult.success ? (
                <Check className="w-5 h-5 text-success-600" />
              ) : (
                <AlertCircle className="w-5 h-5 text-danger-600" />
              )}
              <span className={applyResult.success ? 'text-green-700 dark:text-green-300' : 'text-danger-700 dark:text-red-300'}>
                {applyResult.message}
              </span>
            </div>

            {applyResult.success && applyResult.backupId && (
              <div className="mt-4 flex gap-4">
                <button
                  onClick={handleRollback}
                  className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  Deshacer cambios
                </button>
                {selectedCategory && (
                  <a
                    href={`https://www.jvsinformatica.es/es/${getSpanishContent().link_rewrite}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg flex items-center gap-2"
                  >
                    Ver en web
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!selectedCategory && !isSearching && searchResults.length === 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-12 text-center">
          <Zap className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
            Optimiza tus categorías
          </h2>
          <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto">
            Busca una categoría por ID o nombre para mejorar su contenido SEO usando IA.
          </p>
        </div>
      )}
    </div>
  )
}

// Comparison Field Component
interface ComparisonFieldProps {
  label: string
  field: string
  current: string
  optimized: string
  isEditing: boolean
  isEdited: boolean
  onStartEdit: () => void
  onSave: (value: string) => void
  onCancel: () => void
  maxLength?: number
  showCharCount?: boolean
}

function ComparisonField({
  label,
  field: _field,
  current,
  optimized,
  isEditing,
  isEdited,
  onStartEdit,
  onSave,
  onCancel,
  maxLength,
  showCharCount
}: ComparisonFieldProps) {
  const [editValue, setEditValue] = useState(optimized)

  useEffect(() => {
    setEditValue(optimized)
  }, [optimized, isEditing])

  const hasChanged = current !== optimized

  if (isEditing) {
    return (
      <div className="border border-blue-300 dark:border-blue-600 rounded-lg p-3 bg-primary-50 dark:bg-primary-900/20">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {label}
        </label>
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          maxLength={maxLength}
          className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
          autoFocus
        />
        {showCharCount && (
          <div className="text-xs text-slate-500 mt-1">
            {editValue.length}{maxLength ? `/${maxLength}` : ''} caracteres
          </div>
        )}
        <div className="flex gap-2 mt-2">
          <button
            onClick={() => onSave(editValue)}
            className="px-3 py-1 bg-green-600 text-white rounded text-sm flex items-center gap-1"
          >
            <Save className="w-3 h-3" /> Guardar
          </button>
          <button onClick={onCancel} className="px-3 py-1 bg-gray-300 dark:bg-gray-600 rounded text-sm">
            Cancelar
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium text-slate-700 dark:text-slate-300">
          {label}
          {isEdited && <span className="ml-2 text-xs text-primary-600">(editado)</span>}
        </span>
        <button
          onClick={onStartEdit}
          className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
        >
          <Pencil className="w-4 h-4 text-slate-500" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-xs text-slate-500 mb-1 block">ANTES</span>
          <p className="text-slate-600 dark:text-slate-400 bg-danger-50 dark:bg-danger-900/20 p-2 rounded">
            {current || <span className="italic">Sin definir</span>}
          </p>
        </div>
        <div>
          <span className="text-xs text-slate-500 mb-1 block">DESPUÉS</span>
          <p className={`p-2 rounded ${
            isEdited
              ? 'bg-primary-50 dark:bg-primary-900/20 text-blue-700 dark:text-blue-300'
              : hasChanged
                ? 'bg-success-50 dark:bg-success-900/20 text-green-700 dark:text-green-300'
                : 'bg-slate-50 dark:bg-slate-900 text-slate-600 dark:text-slate-400'
          }`}>
            {optimized || <span className="italic">Sin definir</span>}
          </p>
          {showCharCount && (
            <div className="text-xs text-slate-500 mt-1">
              {optimized.length} caracteres
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
